import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useSite } from "@/lib/useSite";
import { PublicLayout, PageIntro, FinalCta, Reveal, ImageReveal } from "@/components/site";
import { PROCESS_STEPS } from "@/lib/siteContent";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BOOKING_URL } from "@/lib/booking";

export default function About() {
  const { settings, markets } = useSite();
  return (
    <PublicLayout>
      <PageIntro label="About" title="Quiet luxury. Modern real estate media." text="Mandalay is a real estate media studio. We photograph, film and produce listing media for agents who want their properties to be impossible to ignore." />
      <section className="px-6 sm:px-10 lg:px-16 py-20 md:py-28 bg-[#0B0C0E]">
        <ImageReveal src={settings.about_image_url} className="aspect-[16/9] md:h-[80vh] md:aspect-auto" />
      </section>
      <section className="px-6 sm:px-10 lg:px-16 pb-28 grid lg:grid-cols-12 gap-12 bg-[#0B0C0E]">
        <Reveal className="lg:col-span-5">
          <div className="eyebrow">How we work</div>
          <div className="mt-8 space-y-8">
            {PROCESS_STEPS.map(([n, t, d]) => (
              <div key={n} className="flex gap-6"><span className="text-[11px] tracking-[0.2em] text-[#E5B869] pt-1.5">{n}</span><div><div className="font-display text-4xl text-white">{t}</div><p className="text-zinc-400 text-sm mt-2">{d}</p></div></div>
            ))}
          </div>
        </Reveal>
        <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.1}>
          <div className="eyebrow">Serving</div>
          <div className="mt-6">{markets.map((m) => <div key={m.id} data-testid={`about-market-${m.id}`} className="font-display text-5xl md:text-6xl uppercase leading-none text-white">{m.name}</div>)}</div>
          <div className="eyebrow mt-14">Contact</div>
          <a href={`mailto:${settings.contact_email}`} data-testid="about-contact-email" className="block mt-4 text-lg text-white link-underline w-fit">{settings.contact_email}</a>
          {settings.contact_phone && <div data-testid="about-contact-phone" className="mt-2 text-lg text-white">{settings.contact_phone}</div>}
        </Reveal>
      </section>
      <FinalCta image={settings.cta_image_url} />
    </PublicLayout>
  );
}

export function Contact() {
  const { settings } = useSite();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try { await api.post("/contact", form); setSent(true); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const inp = "mt-2 rounded-none border-0 border-b border-white/25 bg-transparent px-0 text-white focus-visible:ring-0 focus-visible:border-[#E5B869] h-11";
  return (
    <PublicLayout>
      <PageIntro label="Contact" title="Let's talk about your next listing." />
      <section className="px-6 sm:px-10 lg:px-16 pb-32 grid lg:grid-cols-12 gap-12 bg-[#0B0C0E]">
        <Reveal className="lg:col-span-4">
          <a href={`mailto:${settings.contact_email}`} data-testid="contact-email" className="text-lg text-white link-underline w-fit block">{settings.contact_email}</a>
          {settings.contact_phone && <div data-testid="contact-phone-display" className="mt-2 text-lg text-white">{settings.contact_phone}</div>}
          <p className="text-zinc-400 mt-8 max-w-sm leading-relaxed">Ready to book? Skip the form and <a href={BOOKING_URL} data-testid="contact-book-link" className="underline underline-offset-4 text-[#E5B869]">schedule a shoot</a> in a few minutes.</p>
        </Reveal>
        <Reveal className="lg:col-span-7 lg:col-start-6" delay={0.1}>
          {sent ? (
            <div data-testid="contact-success"><h2 className="font-display text-5xl text-white">Thank you.</h2><p className="text-zinc-400 mt-3">We'll be in touch shortly.</p></div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
              <div className="grid sm:grid-cols-2 gap-8">
                <label className="block"><span className="eyebrow">Name</span><Input data-testid="contact-name" required value={form.name} onChange={set("name")} className={inp} /></label>
                <label className="block"><span className="eyebrow">Email</span><Input data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className={inp} /></label>
              </div>
              <label className="block"><span className="eyebrow">Phone</span><Input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className={inp} /></label>
              <label className="block"><span className="eyebrow">Message</span><Textarea data-testid="contact-message" required rows={4} value={form.message} onChange={set("message")} className="mt-2 rounded-none border-0 border-b border-white/25 bg-transparent px-0 text-white focus-visible:ring-0 focus-visible:border-[#E5B869]" /></label>
              <button data-testid="contact-submit" disabled={sending} className="btn-dark disabled:opacity-50">{sending ? "Sending…" : "Send"}</button>
            </form>
          )}
        </Reveal>
      </section>
    </PublicLayout>
  );
}

export function CalendarConnected() {
  const status = new URLSearchParams(window.location.search).get("status") || "connected";
  const copy = {
    connected: ["Calendar reconnected.", "Thanks — your Google Calendar is synced with Mandalay again. Shoots will appear on your calendar and your availability is protected."],
    error: ["Something went wrong.", "We couldn't complete the Google connection. Please try the link in your email again, or contact admin@mandalaymedia.com."],
    invalid: ["This link has expired.", "Reconnect links are valid for 7 days. Please ask the Mandalay team to send you a fresh one."],
  }[status] || ["Calendar reconnected.", ""];
  return (
    <PublicLayout>
      <PageIntro label="Google Calendar" title={copy[0]} text={copy[1]}>
        <div data-testid={`calendar-connected-${status}`} className="mt-10"><Link to="/" className="btn-dark">Back to site</Link></div>
      </PageIntro>
    </PublicLayout>
  );
}

export function Legal({ kind }) {
  const title = kind === "privacy" ? "Privacy Policy" : "Terms of Service";
  return (
    <PublicLayout>
      <PageIntro label="Legal" title={title} />
      <section className="px-6 md:px-12 pb-32 max-w-2xl text-slate-500 leading-relaxed space-y-6">
        {kind === "privacy" ? (
          <>
            <p>We collect only the information needed to schedule, produce and deliver your listing media: contact details, property address and booking preferences.</p>
            <p>Delivered media is stored securely and is accessible only to the account that booked the shoot and to Mandalay staff. We never sell your information.</p>
            <p>Questions: admin@mandalaymedia.com</p>
          </>
        ) : (
          <>
            <p>Bookings are confirmed by email. Cancellations or rescheduling requested less than 24 hours before a shoot may incur a fee.</p>
            <p>Delivered media is licensed to the booking agent for marketing the specified property. Mandalay retains copyright and may use work for portfolio purposes.</p>
            <p>Questions: admin@mandalaymedia.com</p>
          </>
        )}
      </section>
    </PublicLayout>
  );
}
